'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Trash2, ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';
import { feedingService, type FeedingLog } from '@/lib/services/farmService';

type SortKey = 'feedingDate' | 'bedId' | 'feedQuantityKg';

export default function FeedingHistoryTable() {
  const [logs, setLogs] = useState<FeedingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('feedingDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await feedingService.getAll();
      setLogs(data);
    } catch (err: any) {
      toast.error('Failed to load feeding logs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const filtered = logs
    .filter((l) =>
      l.bedId.toLowerCase().includes(search.toLowerCase()) ||
      l.feedType.toLowerCase().includes(search.toLowerCase()) ||
      (l.loggedBy ?? '').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const mult = sortDir === 'asc' ? 1 : -1;
      if (sortKey === 'feedQuantityKg') return (a.feedQuantityKg - b.feedQuantityKg) * mult;
      const aVal = sortKey === 'feedingDate' ? a.feedingDate : a.bedId;
      const bVal = sortKey === 'feedingDate' ? b.feedingDate : b.bedId;
      return aVal.localeCompare(bVal) * mult;
    });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await feedingService.delete(deleteId);
    if (ok) {
      setLogs((prev) => prev.filter((l) => l.id !== deleteId));
      toast.success('Feeding log deleted');
    } else {
      toast.error('Failed to delete feeding log');
    }
    setDeleteId(null);
  };

  const SortIcon = ({ col }: { col: SortKey }) =>
    sortKey === col ? (
      sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
    ) : (
      <ChevronDown size={12} className="opacity-30" />
    );

  const moistureColor = (m?: string) => {
    if (!m) return 'text-muted-foreground';
    return m.includes('Optimal') ? 'text-success' : m.includes('Too dry') ? 'text-warning-foreground' : 'text-danger';
  };

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm">
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <h2 className="text-base font-semibold text-foreground flex-1">Feeding History</h2>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-7 pr-3 py-2 text-sm rounded-lg border border-border bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring w-36"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-3 py-2.5 text-left">
                  <button onClick={() => toggleSort('bedId')} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors">
                    Bed <SortIcon col="bedId" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Feed Type</th>
                <th className="px-3 py-2.5 text-left">
                  <button onClick={() => toggleSort('feedQuantityKg')} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors">
                    Qty (kg) <SortIcon col="feedQuantityKg" />
                  </button>
                </th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bedding</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Moisture</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">pH</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">Logged By</th>
                <th className="px-3 py-2.5 text-left">
                  <button onClick={() => toggleSort('feedingDate')} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors">
                    Date <SortIcon col="feedingDate" />
                  </button>
                </th>
                <th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                    {logs.length === 0 ? 'No feeding logs yet — save your first record above' : 'No feeding logs match your search'}
                  </td>
                </tr>
              )}
              {filtered.map((log) => (
                <tr key={log.id} className="hover:bg-muted/40 transition-colors">
                  <td className="px-3 py-3 font-semibold text-foreground">{log.bedId}</td>
                  <td className="px-3 py-3 text-foreground">{log.feedType}</td>
                  <td className="px-3 py-3 tabular-nums font-semibold text-foreground">{log.feedQuantityKg}</td>
                  <td className="px-3 py-3 text-muted-foreground">{log.beddingMaterial || '—'}</td>
                  <td className={`px-3 py-3 text-xs font-medium ${moistureColor(log.moistureLevel)}`}>
                    {log.moistureLevel ? log.moistureLevel.split(' ').slice(0, 2).join(' ') : '—'}
                  </td>
                  <td className="px-3 py-3 tabular-nums text-foreground">{log.phReading ?? '—'}</td>
                  <td className="px-3 py-3 text-muted-foreground">{log.loggedBy || '—'}</td>
                  <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{log.feedingDate}</td>
                  <td className="px-3 py-3">
                    <button
                      onClick={() => setDeleteId(log.id)}
                      className="p-1.5 rounded-lg hover:bg-danger-bg text-muted-foreground hover:text-danger transition-colors"
                      title="Delete this feeding log — this cannot be undone"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-3 border-t border-border flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{filtered.length} of {logs.length} records</span>
      </div>

      <ConfirmModal
        open={!!deleteId}
        title="Delete feeding log?"
        message="This feeding record will be permanently removed. This cannot be undone."
        confirmLabel="Delete Log"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}